/**
 * Website → 📒 Daily log: everything the phone app did, live, in tabs:
 *   💊 Medicines (default): doses grouped Morning / Afternoon / Night, name only.
 *   📱 Activity: reminders, syncs, phone problems, and the phones (owner can remove one).
 *   📋 All: everything together (as before).
 *   Day:        timeline of planned doses (taken at / skipped / not marked / missed,
 *               who and from where), BP readings and checks, phone events,
 *               summary, phone health, stock, last 7 days.
 *   Week/Month: grid of dose times × days, taken-on-time per medicine.
 * Editors can correct a dose (up to 7 days back); print and CSV export.
 */
import { useEffect, useMemo, useState } from 'react';
import { addDays, dialysisMedsOn, logStartOf, BP_LEVEL_TEXT, bpScheduleFor, formatDateISO, formatTime12, localTodayISO, parseDateISO, type Person } from '@bhorosha/core';
import type { BhStore, DoseLogStatus, MonitorSnapshot } from '@bhorosha/data';
import { backend } from '../data';
import { Modal, Notice, Spinner, initials } from '../ui/components';
import {
  csvEscape, datesBetween, dayEntries, eventText, FROM_TEXT, gridFor, phoneHealth, STATUS_TEXT, stockList, summarize,
  type DoseEntry, type Entry, type PersonData,
} from './model';
import { useMonitor } from './useMonitor';
import { BpForm } from './BpForm';
import { fmtDayDMY, fmtDMYTime } from '../ui/dates';

type View = 'day' | 'week' | 'month';
type Tab = 'med' | 'bp' | 'act' | 'all';
// Blood pressure has its own page (❤️ BP, BpPage.tsx), which uses BpTab below.
const TABS: [Tab, string][] = [['med', '💊 Medicines'], ['act', '📱 Activity'], ['all', '📋 All']];

/** Morning 4:00–11:59 · Afternoon 12:00–16:59 · Night 17:00–3:59. */
export type Band = 'morning' | 'afternoon' | 'night';
export const BAND_TEXT: Record<Band, string> = { morning: '🌅 Morning', afternoon: '☀️ Afternoon', night: '🌙 Night' };
export function bandOf(hhmm: string): Band { const h = Number(hhmm.slice(0, 2)); return h >= 4 && h < 12 ? 'morning' : h >= 12 && h < 17 ? 'afternoon' : 'night'; }
/** Sort key inside a day: times after midnight (before 4 AM) come at the end of the night. */
export const daySortKey = (hhmm: string) => (Number(hhmm.slice(0, 2)) < 4 ? '2' : '1') + hhmm;
const isOwnerHere = (store: BhStore) => store.role === 'owner' || window.__bhMe?.role === 'owner';
const CORRECT_DAYS_BACK = 7;

export function memberName(userId: string | null | undefined): string {
  if (!userId) return '';
  const me = window.__bhMe;
  if (me && me.userId === userId) return 'you';
  return (window.__bhMembers || []).find(m => m.userId === userId)?.displayName || 'a family member';
}
export function memberCanSee(userId: string, personId: string): boolean {
  const m = (window.__bhMembers || []).find(x => x.userId === userId);
  if (!m) return false;
  return m.role === 'owner' || m.allPatients || (m.patientIds || []).includes(personId) || window.__bhMe?.role !== 'owner';
}
const fmtDay = (iso: string) => fmtDayDMY(iso, 'long');
const fmtShort = (iso: string) => parseDateISO(iso)!.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', timeZone: 'UTC' });
const fmtClock = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true }) : '');
const ago = (iso?: string | null) => {
  if (!iso) return 'never';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 2) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 48 * 60) return `${Math.round(m / 60)} hours ago`;
  return `${Math.round(m / 1440)} days ago`;
};

function rangeFor(view: View, date: string): [string, string] {
  if (view === 'day') return [formatDateISO(addDays(parseDateISO(date)!, -6)), date]; // the last 7 days feed the mini grid
  if (view === 'week') return [formatDateISO(addDays(parseDateISO(date)!, -6)), date];
  const d = parseDateISO(date)!;
  const first = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
  return [formatDateISO(first), formatDateISO(last)];
}

export function DailyLog({ store, familyId, active }: { store: BhStore; familyId: string; active: boolean }) {
  const [view, setView] = useState<View>('day');
  const [tab, setTab] = useState<Tab>('med');
  const [date, setDate] = useState(localTodayISO());
  const [personId, setPersonId] = useState<string>(() => window.state?.activePersonId || 'all');
  const [from, to] = rangeFor(view, date);
  const { snap, people, loading, error, now, reload } = useMonitor(store, familyId, from, to, active);
  const [fix, setFix] = useState<DoseEntry | null>(null);
  const canEdit = store.role !== 'viewer';

  // Follow the person chosen at the top of the page.
  useEffect(() => {
    const on = (e: Event) => { const id = (e as CustomEvent).detail; if (id) setPersonId(id); };
    window.addEventListener('bh:person', on);
    return () => window.removeEventListener('bh:person', on);
  }, []);

  const shown = useMemo(() => personId === 'all' ? people : people.filter(p => p.person.id === personId), [people, personId]);
  const today = localTodayISO(now);
  const step = (n: number) => {
    const d = parseDateISO(date)!;
    const next = view === 'month' ? new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, Math.min(d.getUTCDate(), 28))) : addDays(d, n * (view === 'week' ? 7 : 1));
    const iso = formatDateISO(next);
    setDate(iso > today ? today : iso);
  };

  if (!active) return null;
  return (
    <div className="bh-log">
      <div className="bh-log-tools no-print">
        <select value={personId} onChange={e => setPersonId(e.target.value)} aria-label="Person">
          <option value="all">Everyone I can see</option>
          {people.map(p => <option key={p.person.id} value={p.person.id}>{p.person.name || 'Unnamed'}</option>)}
        </select>
        <div className="bh-seg">
          {(['day', 'week', 'month'] as View[]).map(v => <button key={v} className={view === v ? 'on' : ''} onClick={() => setView(v)}>{v[0].toUpperCase() + v.slice(1)}</button>)}
        </div>
        <span className="bh-log-datenav">
          <button className="bh-btn bh-btn-ghost" onClick={() => step(-1)} aria-label="Earlier">‹</button>
          <input type="date" value={date} max={today} onChange={e => e.target.value && setDate(e.target.value)} aria-label="Date" />
          <button className="bh-btn bh-btn-ghost" onClick={() => step(1)} disabled={date >= today} aria-label="Later">›</button>
          {date !== today && <button className="bh-btn bh-btn-ghost" onClick={() => setDate(today)}>Today</button>}
        </span>
        <span className="bh-log-spacer" />
        <button className="bh-btn bh-btn-ghost" onClick={() => printReport(view, date, from, to, shown, snap, now, tab)}>🖨 Print</button>
        <button className="bh-btn bh-btn-ghost" onClick={() => downloadCsv(view === 'day' ? date : from, view === 'day' ? date : to, shown, snap, now, tab)}>⬇ CSV</button>
        <span className="bh-live" title="Updates by itself when something changes">● Live</span>
      </div>
      <div className="bh-log-tabs no-print" role="tablist">
        {TABS.map(([id, label]) => <button key={id} role="tab" aria-selected={tab === id} className={tab === id ? 'on' : ''} onClick={() => setTab(id)}>{label}</button>)}
      </div>
      {(() => {
        // The owner's "Start the phone log from" (same for the whole family).
        const ls = people.length ? logStartOf(people[0].person) : null;
        const first = view === 'day' ? date : from;
        if (!ls || first > ls.date) return null;
        return <div className="bh-logstart-note">📌 The phone log starts {fmtDayDMY(ls.date)}, {formatTime12(ls.time)}. Nothing before then is reminded or counted as missed (set by the family owner in Family &amp; members → Family).</div>;
      })()}
      {error && <Notice tone="warn">Could not load the log: {error}. If the database update for the phone app hasn't run yet, see docs/MOBILE.md.</Notice>}
      {loading ? <Spinner /> : tab === 'act'
        ? shown.map(pd => <ActivityView key={pd.person.id} pd={pd} dates={view === 'day' ? [date] : datesBetween(from, to)} snap={snap} now={now} familyId={familyId} canRemove={isOwnerHere(store)} onChanged={reload} showName={personId === 'all'} />)
        : view === 'day'
        ? shown.map(pd => <DayView key={pd.person.id} pd={pd} date={date} snap={snap} now={now} canEdit={canEdit} onFix={setFix} showName={personId === 'all'} tab={tab}
            familyId={familyId} canRemove={isOwnerHere(store)} onChanged={reload} />)
        : shown.map(pd => <GridView key={pd.person.id} pd={pd} dates={datesBetween(from, to)} snap={snap} now={now} withBp={tab === 'all'} onDay={d => { setDate(d); setView('day'); setPersonId(pd.person.id); }} />)}
      {!loading && !shown.length && <p className="bh-muted">No people to show.</p>}
      {fix && <FixDose entry={fix} familyId={familyId} onClose={() => setFix(null)} onSaved={() => { setFix(null); void reload(); window.__bhReload?.(); }} />}
    </div>
  );
}

// ---------------- day ----------------
function DayView({ pd, date, snap, now, canEdit, onFix, showName, tab, familyId, canRemove, onChanged }: {
  pd: PersonData; date: string; snap: MonitorSnapshot; now: Date; canEdit: boolean; onFix: (e: DoseEntry) => void; showName: boolean;
  tab: Tab; familyId: string; canRemove: boolean; onChanged: () => void;
}) {
  const all = dayEntries(pd, date, snap, now, memberCanSee);
  const entries = tab === 'med' ? all.filter(e => e.kind === 'dose') : all;
  const s = summarize(all, pd.person, date);
  const oldest = formatDateISO(addDays(parseDateISO(localTodayISO(now))!, -CORRECT_DAYS_BACK));
  const canFix = canEdit && date >= oldest;
  const phones = snap.devices.filter(d => d.userId && memberCanSee(d.userId, pd.person.id) && notifiesUser(pd.person, d.userId));
  const week = datesBetween(formatDateISO(addDays(parseDateISO(date)!, -6)), date);
  return (
    <section className="bh-log-person">
      {showName && <h3 className="bh-log-name"><span className="bh-acct-avatar">{initials(pd.person.name)}</span>{pd.person.name}</h3>}
      <div className="bh-log-day-title">{fmtDay(date)}</div>
      <div className="bh-log-grid">
        <div>
          <div className="bh-log-sum">
            <div><b>{s.taken} of {s.due}</b><span>doses taken so far{s.planned > s.due ? ` (${s.planned} today)` : ''}</span></div>
            <div><b>{s.skipped}</b><span>skipped</span></div>
            <div className={s.notMarked + s.missed ? 'bad' : ''}><b>{s.notMarked + s.missed}</b><span>not marked / missed</span></div>
            {tab === 'all' && <div><b>{s.bpDone}{s.bpPlanned ? ` of ${s.bpPlanned}` : ''}</b><span>BP checks</span></div>}
          </div>
          {!entries.length && <p className="bh-muted">{tab === 'med' ? 'No medicines planned or marked on this day.' : 'Nothing planned or logged on this day.'}</p>}
          {tab === 'med'
            ? <MedBands entries={entries as DoseEntry[]} canFix={canFix} onFix={onFix} />
            : <div className="bh-tl">{entries.map(e => <TimelineItem key={e.key} e={e} canFix={canFix} onFix={onFix} />)}</div>}
          <AtDialysis pd={pd} date={date} snap={snap} />
          {canEdit && !canFix && <p className="bh-small bh-muted">Doses can be corrected on the website up to {CORRECT_DAYS_BACK} days back.</p>}
        </div>
        <aside className="bh-log-side">
          {tab === 'all' && <PhonesBox pd={pd} phones={phones} now={now} familyId={familyId} canRemove={canRemove} onChanged={onChanged} />}
          <div className="bh-box">
            <h4>💊 Medicine in hand now</h4>
            {stockList(pd).map(m => (
              <div key={m.id} className="bh-kv"><span>{m.name}</span><b className={m.low ? 'bad' : ''}>{fmtQty(m.onHand)}{m.daysLeft !== null ? ` · ${m.daysLeft} d` : ''}</b></div>
            ))}
            {!pd.meds.length && <p className="bh-small bh-muted">No medicines.</p>}
          </div>
          <div className="bh-box">
            <h4>Last 7 days</h4>
            <MiniGrid pd={pd} dates={week} snap={snap} now={now} />
          </div>
        </aside>
      </div>
    </section>
  );
}
function notifiesUser(p: Person, userId: string) {
  const ids = (p as { notify_user_ids?: string[] }).notify_user_ids;
  return !Array.isArray(ids) || ids.includes(userId);
}
const fmtQty = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

function TimelineItem({ e, canFix, onFix, nameOnly }: { e: Entry; canFix: boolean; onFix: (e: DoseEntry) => void; nameOnly?: boolean }) {
  if (e.kind === 'dose') {
    const log = e.log;
    const who = log ? `${memberName(log.loggedBy)} ${FROM_TEXT[log.loggedFrom || 'mobile'] || ''}`.trim() : e.fromReminderPage ? 'on the Reminder page' : '';
    const corrected = log && log.changes.length > 1 ? log.changes[log.changes.length - 1] : null;
    return (
      <div className={`bh-tli ${e.status}`}>
        <span className="bh-tm">{formatTime12(e.time)}</span>
        <div className="bh-tli-h">
          {e.dose.name}{e.dose.strength ? ` ${e.dose.strength}` : ''}{!nameOnly && <span className="bh-muted"> · {e.dose.amountLabel} · {e.dose.label}</span>}
          <span className={`bh-st ${e.status}`}>{e.status === 'taken' && log?.takenAt ? `Taken at ${fmtClock(log.takenAt)}` : STATUS_TEXT[e.status]}</span>
          {e.lateMinutes !== null && Math.abs(e.lateMinutes) >= 15 && <span className="bh-st later">{e.lateMinutes > 0 ? `${e.lateMinutes} min late` : `${-e.lateMinutes} min early`}</span>}
          {canFix && e.status !== 'later' && <button className="bh-link" onClick={() => onFix(e)}>Correct</button>}
        </div>
        <div className="bh-tli-d">
          {who && <>{who}{log?.updatedAt && log.status !== 'taken' ? ` at ${fmtClock(log.updatedAt)}` : ''}</>}
          {log?.reason && <> · reason: {log.reason}</>}
          {log?.note && <> · note: {log.note}</>}
          {log?.status === 'taken' && e.dose.medicineId && <> · stock −{fmtQty(log.amount)}</>}
          {corrected && <> · changed {FROM_TEXT[corrected.from] || ''} by {memberName(corrected.by)} at {fmtClock(corrected.at)} (was: {corrected.old ? STATUS_TEXT[corrected.old as DoseLogStatus] : 'Not marked'})</>}
        </div>
      </div>
    );
  }
  if (e.kind === 'bp') {
    const r = e.reading;
    return (
      <div className="bh-tli bp">
        <span className="bh-tm">{formatTime12(e.time)}</span>
        <div className="bh-tli-h">Blood pressure {r.systolic}/{r.diastolic}{r.pulse ? ` · pulse ${r.pulse}` : ''}
          <span className={`bh-st bp-${r.level}`}>{BP_LEVEL_TEXT[r.level].word}</span></div>
        <div className="bh-tli-d">{memberName(r.loggedBy)} {FROM_TEXT[r.loggedFrom || 'mobile']}{r.note ? ` · ${r.note}` : ''}</div>
      </div>
    );
  }
  if (e.kind === 'bpcheck') {
    return (
      <div className={`bh-tli ${e.status === 'not_done' ? 'missed' : 'later'}`}>
        <span className="bh-tm">{formatTime12(e.time)}</span>
        <div className="bh-tli-h">Blood pressure check <span className={`bh-st ${e.status === 'not_done' ? 'missed' : 'later'}`}>{e.status === 'not_done' ? 'Not done' : 'Later'}</span></div>
      </div>
    );
  }
  return (
    <div className="bh-tli ev">
      <span className="bh-tm">{formatTime12(e.time)}</span>
      <div className="bh-tli-d">📱 {eventText(e.event)}{e.event.userId ? ` · ${memberName(e.event.userId)}` : ''}</div>
    </div>
  );
}

function MiniGrid({ pd, dates, snap, now }: { pd: PersonData; dates: string[]; snap: MonitorSnapshot; now: Date }) {
  const { rows, perMed } = gridFor(pd, dates, snap, now);
  const planned = perMed.reduce((s, m) => s + m.planned, 0), taken = perMed.reduce((s, m) => s + m.taken, 0);
  if (!rows.length) return <p className="bh-small bh-muted">No doses planned.</p>;
  return (
    <>
      <div className="bh-mini" style={{ gridTemplateColumns: `64px repeat(${dates.length}, 1fr)` }}>
        <span />{dates.map(d => <span key={d} className="bh-mini-h">{fmtShort(d).split(' ')[0]}</span>)}
        {rows.map(r => [<span key={r.time} className="bh-mini-t">{formatTime12(r.time)}</span>,
          ...dates.map(d => <span key={r.time + d} className={`bh-cell ${r.cells.get(d) || 'none'}`} title={`${fmtDayDMY(d, 'short')} ${formatTime12(r.time)}: ${cellText(r.cells.get(d))}`} />)])}
      </div>
      <div className="bh-small bh-muted" style={{ marginTop: 6 }}>Green taken · grey skipped · red not marked or missed · amber some taken.</div>
      {planned > 0 && <div className="bh-kv" style={{ marginTop: 6 }}><span>Taken this week</span><b>{Math.round((taken / planned) * 100)}%</b></div>}
    </>
  );
}
const cellText = (c?: string) => ({ taken: 'taken', skipped: 'skipped', missed: 'missed', due: 'not marked yet', later: 'later', partial: 'some taken', none: 'nothing planned' } as Record<string, string>)[c || 'none'];


// ---------------- 💊 Medicines: Morning / Afternoon / Night ----------------
function MedBands({ entries, canFix, onFix }: { entries: DoseEntry[]; canFix: boolean; onFix: (e: DoseEntry) => void }) {
  const bands: Band[] = ['morning', 'afternoon', 'night'];
  return (
    <div className="bh-bands">
      {bands.map(b => {
        const list = entries.filter(e => bandOf(e.time) === b).sort((x, y) => daySortKey(x.time).localeCompare(daySortKey(y.time)));
        if (!list.length) return null;
        const taken = list.filter(e => e.status === 'taken').length;
        const missed = list.filter(e => e.status === 'missed').length;
        const notMarked = list.filter(e => e.status === 'due').length;
        const due = list.filter(e => e.status !== 'later').length;
        return (
          <div key={b} className="bh-band">
            <div className="bh-band-h">{BAND_TEXT[b]}<span>{taken} of {list.length} taken{missed ? ` · ${missed} missed` : ''}{notMarked ? ` · ${notMarked} not marked yet` : ''}{due < list.length ? ` · ${list.length - due} later` : ''}</span></div>
            <div className="bh-tl">{list.map(e => <TimelineItem key={e.key} e={e} canFix={canFix} onFix={onFix} nameOnly />)}</div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------- 🩺 At dialysis ----------------
/** "On dialysis days only" medicines: given at the dialysis centre, shown for information, not counted. */
function AtDialysis({ pd, date, snap }: { pd: PersonData; date: string; snap: MonitorSnapshot }) {
  const list = dialysisMedsOn(pd.person, pd.meds, date);
  if (!list.length) return null;
  const marked = (id: string) => snap.doses.find(l => l.patientId === pd.person.id && l.doseDate === date && l.slot === 'dialysis' && l.sourceId === id);
  return (
    <div className="bh-band">
      <div className="bh-band-h">🩺 At dialysis<span>given at the dialysis centre · not counted above</span></div>
      <div className="bh-tl">{list.map(m => {
        const log = marked(m.medicineId);
        return (
          <div key={m.medicineId} className="bh-tli dialysis">
            <span className="bh-tm">{formatTime12(m.time)}</span>
            <div className="bh-tli-h">{m.name}{m.strength ? ` ${m.strength}` : ''}
              <span className="bh-st dialysis">{log?.status === 'taken' ? 'Marked given' : 'Given at dialysis'}</span></div>
            <div className="bh-tli-d">{m.amountLabel}{log?.takenAt ? ` · marked ${fmtClock(log.takenAt)} by ${memberName(log.loggedBy)}` : ''}</div>
          </div>
        );
      })}</div>
    </div>
  );
}

// ---------------- 📱 Phones (owner can remove) ----------------
const ALERT_NAMES: Record<string, string> = { doses: 'Medicines', injections: 'Injections', bp: 'Blood pressure', visits: 'Doctor appointments', vaccines: 'Vaccines' };
function PhonesBox({ pd, phones, now, familyId, canRemove, onChanged }: {
  pd: PersonData; phones: MonitorSnapshot['devices']; now: Date; familyId: string; canRemove: boolean; onChanged: () => void;
}) {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  async function remove(d: MonitorSnapshot['devices'][number]) {
    const name = `${d.model || 'this phone'} (${memberName(d.userId)})`;
    if (!confirm(`Remove ${name} and sign it out?\n\nThe phone is signed out the next time it connects (when the app is opened, or in the background), its reminders stop and its copy of the family data is cleared. Doses and readings it saved stay in the log. Someone with the login can sign in again.`)) return;
    setBusy(d.userId + d.deviceId); setErr('');
    try { await backend.removeDevice(familyId, d.userId!, d.deviceId); onChanged(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(''); }
  }
  return (
    <div className="bh-box">
      <h4>📱 Phones</h4>
      {err && <div className="bh-error">{err}</div>}
      {!phones.length && <p className="bh-small bh-muted">No phone has signed in for {pd.person.name || 'this person'} yet. Make a phone login in Family &amp; members → Phone logins.</p>}
      {phones.map(d => {
        const h = phoneHealth(d, now);
        const off = Object.entries(d.alertPrefs || {}).filter(([, on]) => on === false).map(([k]) => ALERT_NAMES[k] || k);
        return (
          <div key={d.userId + d.deviceId} className="bh-phone">
            <div className="bh-strong bh-phone-top">{memberName(d.userId)}{d.model ? ` · ${d.model}` : ''}
              {canRemove && <button className="bh-link bh-danger" disabled={busy === d.userId + d.deviceId} onClick={() => remove(d)}>Remove</button>}</div>
            <div className="bh-kv"><span>Last synced</span><b className={h.stale ? 'bad' : 'ok'}>{ago(d.lastSyncAt || d.lastSeenAt)}</b></div>
            <div className="bh-kv"><span>Alerts</span><b className={d.notifications === 'blocked' ? 'bad' : 'ok'}>{d.notifications === 'blocked' ? 'Blocked' : d.notifications === 'allowed' ? 'Allowed' : 'Unknown'}</b></div>
            {off.length > 0 && <div className="bh-kv"><span>Turned off</span><b className="bad">{off.join(', ')}</b></div>}
            {d.exactAlarms === false && <div className="bh-kv"><span>Exact alarms</span><b className="bad">Off</b></div>}
            {d.batteryOptimised && <div className="bh-kv"><span>Battery saver</span><b className="bad">On for Bhorosha</b></div>}
            {d.remindersUntil && <div className="bh-kv"><span>Reminders set until</span><span>{fmtDMYTime(d.remindersUntil, true)}</span></div>}
            <div className="bh-kv"><span>App</span><span>{[d.appVersion, d.platform].filter(Boolean).join(' · ') || '—'}</span></div>
            {h.problems.length > 0 && <div className="bh-phone-warn">⚠ {h.problems.join(' · ')}</div>}
          </div>
        );
      })}
    </div>
  );
}
const phonesFor = (pd: PersonData, snap: MonitorSnapshot) => snap.devices.filter(d => d.userId && memberCanSee(d.userId, pd.person.id) && notifiesUser(pd.person, d.userId));

// ---------------- 📱 Activity ----------------
function ActivityView({ pd, dates, snap, now, familyId, canRemove, onChanged, showName }: {
  pd: PersonData; dates: string[]; snap: MonitorSnapshot; now: Date; familyId: string; canRemove: boolean; onChanged: () => void; showName: boolean;
}) {
  const days = dates.slice().reverse().map(d => ({ d, ev: dayEntries(pd, d, snap, now, memberCanSee).filter(e => e.kind === 'event').reverse() })).filter(x => x.ev.length);
  return (
    <section className="bh-log-person">
      {showName && <h3 className="bh-log-name"><span className="bh-acct-avatar">{initials(pd.person.name)}</span>{pd.person.name}</h3>}
      <div className="bh-log-grid">
        <div>
          {!days.length && <p className="bh-muted">No phone activity on {dates.length === 1 ? 'this day' : 'these days'}.</p>}
          {days.map(x => (
            <div key={x.d} className="bh-band">
              <div className="bh-band-h">{fmtDay(x.d)}<span>{x.ev.length} event{x.ev.length === 1 ? '' : 's'}</span></div>
              <div className="bh-tl">{x.ev.map(e => <TimelineItem key={e.key} e={e} canFix={false} onFix={() => {}} />)}</div>
            </div>
          ))}
        </div>
        <aside className="bh-log-side"><PhonesBox pd={pd} phones={phonesFor(pd, snap)} now={now} familyId={familyId} canRemove={canRemove} onChanged={onChanged} /></aside>
      </div>
    </section>
  );
}

// ---------------- ❤️ Blood pressure ----------------
/** "🌅 Morning"; on phones only the picture (the word is in the title attribute). */
function BandLabel({ b }: { b: Band }) {
  const [ico, ...word] = BAND_TEXT[b].split(' ');
  return <span title={word.join(' ')}>{ico}<span className="bh-band-word"> {word.join(' ')}</span></span>;
}
/** The day a reading belongs to: before 4 AM counts for the night before. */
function bpDay(iso: string): { day: string; time: string } {
  const d = new Date(iso);
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const x = d.getHours() < 4 ? new Date(d.getTime() - 4 * 3600000) : d;
  return { day: formatDateISO(new Date(Date.UTC(x.getFullYear(), x.getMonth(), x.getDate()))), time };
}
interface BpRow { reading?: MonitorSnapshot['bp'][number]; id?: string; patientId?: string; loggedBy?: string | null; band: Band; time: string; sys: number; dia: number; pulse: number | null; level: string; by: string; note: string; check?: boolean }
function bpRowsByDay(pd: PersonData, dates: string[], readings: MonitorSnapshot['bp'], snap: MonitorSnapshot, now: Date, withChecks: boolean) {
  const out = new Map<string, BpRow[]>();
  for (const r of readings.filter(x => x.patientId === pd.person.id)) {
    const { day, time } = bpDay(r.measuredAt);
    if (!dates.includes(day)) continue;
    out.set(day, [...(out.get(day) || []), { reading: r, id: r.id, patientId: r.patientId, loggedBy: r.loggedBy, band: bandOf(time), time, sys: r.systolic, dia: r.diastolic, pulse: r.pulse ?? null, level: r.level,
      by: `${FROM_TEXT[r.loggedFrom || 'mobile'] === FROM_TEXT.web ? 'Website' : 'Phone'}${r.loggedBy ? ' · ' + memberName(r.loggedBy) : ''}`, note: r.note || '' }]);
  }
  if (withChecks) for (const d of dates) {
    for (const e of dayEntries(pd, d, snap, now)) if (e.kind === 'bpcheck' && e.status === 'not_done')
      out.set(d, [...(out.get(d) || []), { band: bandOf(e.time), time: e.time, sys: 0, dia: 0, pulse: null, level: '', by: '', note: '', check: true }]);
  }
  for (const [d, rows] of out) out.set(d, rows.sort((a, b) => daySortKey(a.time).localeCompare(daySortKey(b.time))));
  return out;
}

export function BpTab({ people, dates, snap, now, familyId, canExport, showName, canEdit, onChanged }: {
  people: PersonData[]; dates: string[]; snap: MonitorSnapshot; now: Date; familyId: string; canExport: boolean; showName: boolean;
  canEdit: boolean; onChanged: () => void;
}) {
  const [editing, setEditing] = useState<{ row: BpRow; pd: PersonData } | null>(null);
  // The owner can change or remove any reading; others only the readings they saved.
  const canRemove = (r: BpRow) => !r.check && !!r.id && (canExport || (canEdit && !!r.loggedBy && r.loggedBy === window.__bhMe?.userId));
  async function removeReading(r: BpRow, who: string) {
    if (!confirm(`Remove the reading ${r.sys}/${r.dia} at ${formatTime12(r.time)} for ${who}?\n\nIt is taken out of the log, the chart and exports. This can't be undone.`)) return;
    try { await backend.deleteBp(familyId, r.patientId!, r.id!); onChanged(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }
  const [from, setFrom] = useState(dates[0]);
  const [to, setTo] = useState(dates[dates.length - 1]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { setFrom(dates[0]); setTo(dates[dates.length - 1]); }, [dates[0], dates[dates.length - 1]]); // eslint-disable-line react-hooks/exhaustive-deps
  async function exportBp(kind: 'csv' | 'print') {
    if (!from || !to || from > to) { setErr('Please pick a "from" date on or before the "to" date.'); return; }
    setBusy(true); setErr('');
    try {
      const rows: string[][] = [];
      const days = datesBetween(from, to);
      for (const pd of people) {
        const readings = await backend.loadBpReadings(familyId, pd.person.id, formatDateISO(addDays(parseDateISO(from)!, -1)), formatDateISO(addDays(parseDateISO(to)!, 1)));
        const byDay = bpRowsByDay(pd, days, readings, snap, now, false);
        for (const d of days.slice().reverse()) for (const r of byDay.get(d) || [])
          rows.push([fmtDayDMY(d), BAND_TEXT[r.band].replace(/^\S+\s/, ''), formatTime12(r.time), String(r.sys), String(r.dia), r.pulse ? String(r.pulse) : '',
            BP_LEVEL_TEXT[r.level as keyof typeof BP_LEVEL_TEXT]?.word || '', pd.person.name || '', r.by, r.note]);
      }
      const head = ['Date', 'Time of day', 'Time', 'Top (systolic)', 'Bottom (diastolic)', 'Pulse', 'Level', 'Person', 'Saved by', 'Note'];
      if (kind === 'csv') saveCsv([head, ...rows], `Bhorosha_BloodPressure_${from}_to_${to}.csv`);
      else printTable(`Blood pressure · ${fmtDayDMY(from)} – ${fmtDayDMY(to)}`, head, rows);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  }
  return (
    <div>
      {canExport && (
        <div className="bh-bp-export no-print">
          <b>⬇ Export blood pressure</b>
          <label>From <input type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
          <label>to <input type="date" value={to} onChange={e => setTo(e.target.value)} /></label>
          <button className="bh-btn bh-btn-primary" disabled={busy} onClick={() => exportBp('csv')}>Download CSV</button>
          <button className="bh-btn bh-btn-ghost" disabled={busy} onClick={() => exportBp('print')}>🖨 Print</button>
          {err && <span className="bh-error">{err}</span>}
        </div>
      )}
      {!canExport && err && <div className="bh-error">{err}</div>}
      {people.map(pd => {
        const byDay = bpRowsByDay(pd, dates, snap.bp, snap, now, true);
        const days = dates.slice().reverse().filter(d => byDay.has(d));
        return (
          <section key={pd.person.id} className="bh-log-person">
            {showName && <h3 className="bh-log-name"><span className="bh-acct-avatar">{initials(pd.person.name)}</span>{pd.person.name}</h3>}
            {!days.length && <p className="bh-muted">No blood pressure readings from {fmtDayDMY(dates[0])} to {fmtDayDMY(dates[dates.length - 1])}.</p>}
            {days.map(d => (
              <div key={d} className="bh-box bh-bp-day">
                <h4>{fmtDay(d)}</h4>
                <table className="bh-bp-table">
                  <thead><tr><th>Time of day</th><th>Time</th><th>Reading</th><th>Pulse</th><th>Level</th><th>Saved by</th><th /></tr></thead>
                  <tbody>{(['morning', 'afternoon', 'night'] as Band[]).map(b => {
                    const rows = byDay.get(d)!.filter(r => r.band === b);
                    if (!rows.length) return <tr key={b} className="bh-bp-none"><td className="bh-strong"><BandLabel b={b} /></td><td>—</td><td colSpan={5} className="bh-muted">No reading</td></tr>;
                    return rows.map((r, i) => (
                      <tr key={b + i}>
                        <td className="bh-strong">{i === 0 ? <BandLabel b={b} /> : ''}</td>
                        <td>{formatTime12(r.time)}</td>
                        {r.check ? <td colSpan={5}><span className="bh-st missed">Check not done</span></td> : <>
                          <td className="bh-bp-v">{r.sys}/{r.dia}</td><td>{r.pulse || '—'}</td>
                          <td><span className={`bh-st bp-${r.level}`}>{BP_LEVEL_TEXT[r.level as keyof typeof BP_LEVEL_TEXT]?.word}</span></td>
                          <td className="bh-small">{r.by}{r.note ? ` · ${r.note}` : ''}</td>
                          <td className="no-print bh-bp-acts">{canRemove(r) && <>
                            <button className="bh-link" onClick={() => setEditing({ row: r, pd })}>Edit</button>
                            <button className="bh-link bh-danger" onClick={() => removeReading(r, pd.person.name || 'this person')}>Remove</button></>}</td></>}
                      </tr>
                    ));
                  })}</tbody>
                </table>
              </div>
            ))}
            {editing && editing.pd.person.id === pd.person.id && editing.row.reading && (
              <Modal title={`Edit reading · ${pd.person.name || ''}`} onClose={() => setEditing(null)}>
                <BpForm familyId={familyId} personId={pd.person.id} limits={bpScheduleFor(pd.person).limits} reading={editing.row.reading}
                  onSaved={() => { setEditing(null); onChanged(); }} onCancel={() => setEditing(null)} />
              </Modal>
            )}
            <p className="bh-small bh-muted">Morning = 4 AM – 11:59 AM · Afternoon = 12 PM – 4:59 PM · Night = 5 PM – 3:59 AM (a reading after midnight counts for the night before).</p>
          </section>
        );
      })}
    </div>
  );
}

// ---------------- week / month ----------------
function GridView({ pd, dates, snap, now, onDay, withBp }: { pd: PersonData; dates: string[]; snap: MonitorSnapshot; now: Date; onDay: (d: string) => void; withBp: boolean }) {
  const { rows, perMed } = gridFor(pd, dates, snap, now);
  const bp = snap.bp.filter(b => b.patientId === pd.person.id);
  return (
    <section className="bh-log-person">
      <h3 className="bh-log-name"><span className="bh-acct-avatar">{initials(pd.person.name)}</span>{pd.person.name} <span className="bh-muted bh-small">{fmtDay(dates[0])} – {fmtDay(dates[dates.length - 1])}</span></h3>
      {!rows.length ? <p className="bh-muted">No doses planned in this period.</p> : (
        <div className="bh-big-grid-wrap">
          <div className="bh-big-grid" style={{ gridTemplateColumns: `90px repeat(${dates.length}, minmax(22px, 1fr))` }}>
            <span />{dates.map(d => <button key={d} className="bh-mini-h bh-day-btn" onClick={() => onDay(d)} title={`Open ${fmtDayDMY(d, 'long')}`}>{fmtShort(d)}</button>)}
            {rows.map(r => [<span key={r.time} className="bh-mini-t">{formatTime12(r.time)}<br /><small>{r.label}</small></span>,
              ...dates.map(d => <button key={r.time + d} className={`bh-cell ${r.cells.get(d) || 'none'}`} onClick={() => onDay(d)} title={`${fmtDayDMY(d, 'short')} ${formatTime12(r.time)}: ${cellText(r.cells.get(d))}`} />)])}
          </div>
        </div>
      )}
      {perMed.length > 0 && (
        <table className="bh-adh"><thead><tr><th>Medicine</th><th>Taken</th><th>of</th><th>%</th></tr></thead>
          <tbody>{perMed.map(m => <tr key={m.name}><td>{m.name}</td><td>{m.taken}</td><td>{m.planned}</td><td className={m.planned && m.taken / m.planned < 0.8 ? 'bad' : ''}>{m.planned ? Math.round((m.taken / m.planned) * 100) : 0}%</td></tr>)}</tbody></table>
      )}
      {withBp && bp.length > 0 && <><h4 style={{ margin: '14px 0 6px' }}>Blood pressure</h4><BpChart readings={bp} person={pd.person} /></>}
    </section>
  );
}

/** Simple SVG chart: top (systolic) and bottom (diastolic) numbers over time, with the person's limits. */
export function BpChart({ readings, person, height = 180 }: { readings: MonitorSnapshot['bp']; person: Person; height?: number }) {
  const W = 640, H = height, P = 30;
  const pts = readings.slice().sort((a, b) => a.measuredAt.localeCompare(b.measuredAt));
  if (!pts.length) return null;
  const t0 = new Date(pts[0].measuredAt).getTime(), t1 = Math.max(t0 + 3600000, new Date(pts[pts.length - 1].measuredAt).getTime());
  const lim = bpScheduleFor(person).limits;
  const y = (v: number) => H - P - ((Math.max(40, Math.min(220, v)) - 40) / 180) * (H - 2 * P);
  const x = (iso: string) => P + ((new Date(iso).getTime() - t0) / (t1 - t0)) * (W - 2 * P);
  const line = (k: 'systolic' | 'diastolic') => pts.map((r, i) => `${i ? 'L' : 'M'}${x(r.measuredAt).toFixed(1)},${y(r[k]).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="bh-bpchart" role="img" aria-label="Blood pressure chart">
      {[60, 90, 120, 140, 180].map(v => <g key={v}><line x1={P} x2={W - P} y1={y(v)} y2={y(v)} className="grid" /><text x={4} y={y(v) + 4}>{v}</text></g>)}
      <line x1={P} x2={W - P} y1={y(lim.high_sys)} y2={y(lim.high_sys)} className="limit" />
      <line x1={P} x2={W - P} y1={y(lim.high_dia)} y2={y(lim.high_dia)} className="limit dia" />
      <path d={line('systolic')} className="sys" /><path d={line('diastolic')} className="dia" />
      {pts.map(r => <g key={r.id}><circle cx={x(r.measuredAt)} cy={y(r.systolic)} r={3.5} className={`pt ${r.level}`}><title>{`${fmtDMYTime(r.measuredAt)} · ${r.systolic}/${r.diastolic}`}</title></circle><circle cx={x(r.measuredAt)} cy={y(r.diastolic)} r={3} className="pt dia" /></g>)}
    </svg>
  );
}

// ---------------- correct a dose ----------------
function FixDose({ entry, familyId, onClose, onSaved }: { entry: DoseEntry; familyId: string; onClose: () => void; onSaved: () => void }) {
  const [status, setStatus] = useState<DoseLogStatus | 'none'>(entry.log?.status || (entry.status === 'taken' ? 'taken' : 'taken'));
  const plannedLocal = `${entry.dose.date}T${entry.time}`;
  const [time, setTime] = useState(() => (entry.log?.takenAt ? toLocalInput(entry.log.takenAt) : plannedLocal));
  const [note, setNote] = useState(entry.log?.note || '');
  const [reason, setReason] = useState(entry.log?.reason || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function save() {
    setBusy(true); setErr('');
    try {
      await backend.markDose(familyId, {
        patientId: entry.person.id, source: entry.dose.source, sourceId: entry.dose.sourceId, medicineId: entry.dose.medicineId,
        date: entry.dose.date, slot: entry.dose.slot, plannedTime: entry.time, name: entry.dose.name, amount: entry.dose.amount, unit: entry.dose.amountLabel,
        status: status === 'none' ? null : status, takenAt: status === 'taken' ? new Date(time).toISOString() : null,
        reason: status === 'skipped' ? reason || null : null, note: note || null, from: 'web',
      });
      onSaved();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  }
  return (
    <Modal title={`Correct: ${entry.dose.name} · ${formatTime12(entry.time)}`} onClose={onClose}>
      <p className="bh-small bh-muted">{entry.person.name} · {fmtDay(entry.dose.date)} · {entry.dose.amountLabel} · {entry.dose.label}. The change is saved in the log with your name, and the medicine in hand is updated the same way as on the phone.</p>
      <div className="bh-fix-opts">
        {(['taken', 'skipped', 'missed', 'none'] as const).map(s => (
          <label key={s} className={`bh-fix-opt ${status === s ? 'on' : ''}`}><input type="radio" checked={status === s} onChange={() => setStatus(s)} /> {s === 'none' ? 'Not marked (undo)' : STATUS_TEXT[s]}</label>
        ))}
      </div>
      {status === 'taken' && <label className="bh-field"><span className="bh-field-label">Taken at</span><input type="datetime-local" value={time} onChange={e => setTime(e.target.value)} /></label>}
      {status === 'skipped' && <label className="bh-field"><span className="bh-field-label">Reason</span><input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Doctor said stop" /></label>}
      <label className="bh-field"><span className="bh-field-label">Note</span><input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. given by Tanvir" /></label>
      {err && <div className="bh-error">{err}</div>}
      <div className="bh-row"><button className="bh-btn bh-btn-primary" disabled={busy} onClick={save}>Save</button><button className="bh-btn bh-btn-ghost" onClick={onClose}>Cancel</button></div>
    </Modal>
  );
}
function toLocalInput(iso: string) {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

// ---------------- print / CSV ----------------
function rowsFor(fromISO: string, toISO: string, people: PersonData[], snap: MonitorSnapshot, now: Date, tab: Tab = 'all') {
  const rows: string[][] = [];
  for (const date of datesBetween(fromISO, toISO)) {
    for (const pd of people) {
      for (const e of dayEntries(pd, date, snap, now, memberCanSee)) {
        const want = tab === 'all' || (tab === 'med' && e.kind === 'dose') || (tab === 'bp' && (e.kind === 'bp' || e.kind === 'bpcheck')) || (tab === 'act' && e.kind === 'event');
        if (!want) continue;
        if (e.kind === 'event') { rows.push([date, formatTime12(e.time), pd.person.name, 'Phone', eventText(e.event), '', '', '', e.event.userId ? memberName(e.event.userId) : '', '', '']); continue; }
        if (e.kind === 'dose') rows.push([date, formatTime12(e.time), pd.person.name, 'Medicine', `${e.dose.name}${e.dose.strength ? ' ' + e.dose.strength : ''}`, e.dose.amountLabel,
          STATUS_TEXT[e.status], e.log?.takenAt ? fmtClock(e.log.takenAt) : '', e.log ? memberName(e.log.loggedBy) : '', e.log ? FROM_TEXT[e.log.loggedFrom || 'mobile'] : e.fromReminderPage ? 'Reminder page' : '', [e.log?.reason, e.log?.note].filter(Boolean).join('; ')]);
        else if (e.kind === 'bp') rows.push([date, formatTime12(e.time), pd.person.name, 'Blood pressure', `${e.reading.systolic}/${e.reading.diastolic}${e.reading.pulse ? ' pulse ' + e.reading.pulse : ''}`, '', BP_LEVEL_TEXT[e.reading.level].word, fmtClock(e.reading.measuredAt), memberName(e.reading.loggedBy), FROM_TEXT[e.reading.loggedFrom || 'mobile'], e.reading.note || '']);
        else if (e.kind === 'bpcheck') rows.push([date, formatTime12(e.time), pd.person.name, 'Blood pressure check', '', '', e.status === 'not_done' ? 'Not done' : 'Later', '', '', '', '']);
      }
    }
  }
  return rows;
}
const HEAD = ['Date', 'Planned', 'Person', 'What', 'Medicine / reading', 'Amount', 'Status', 'Time', 'By', 'From', 'Reason / note'];

function downloadCsv(fromISO: string, toISO: string, people: PersonData[], snap: MonitorSnapshot, now: Date, tab: Tab = 'all') {
  const rows = rowsFor(fromISO, toISO, people, snap, now, tab);
  const csv = '﻿' + [HEAD, ...rows].map(r => r.map(csvEscape).join(',')).join('\r\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  a.download = `Bhorosha_DailyLog_${fromISO}${fromISO !== toISO ? '_to_' + toISO : ''}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

function printReport(view: View, date: string, fromISO: string, toISO: string, people: PersonData[], snap: MonitorSnapshot, now: Date, tab: Tab = 'all') {
  const f = view === 'day' ? date : fromISO, t = view === 'day' ? date : toISO;
  const rows = rowsFor(f, t, people, snap, now, tab);
  const esc = (s: string) => s.replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c]!);
  const sums = people.map(pd => {
    let taken = 0, due = 0, skipped = 0, missed = 0;
    for (const d of datesBetween(f, t)) { const s = summarize(dayEntries(pd, d, snap, now), pd.person, d); taken += s.taken; due += s.due; skipped += s.skipped; missed += s.missed + s.notMarked; }
    return `<p><b>${esc(pd.person.name || '')}</b>: ${taken} of ${due} doses taken, ${skipped} skipped, ${missed} not marked / missed.</p>`;
  }).join('');
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Bhorosha Daily log</title><style>
    @page { size: A4; margin: 12mm; } body { font-family: Segoe UI, Arial, sans-serif; font-size: 11px; color: #222; }
    h1 { font-size: 17px; margin: 0 0 2px; } .sub { color: #666; margin-bottom: 8px; } p { margin: 2px 0; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; } th, td { border-bottom: 1px solid #ddd; padding: 3px 4px; text-align: left; vertical-align: top; }
    th { background: #f0f3f8; } td.Taken { color: #1E7F4F; font-weight: 700; } td.Missed, td.Not { color: #B42318; font-weight: 700; }
  </style></head><body><h1>Bhorosha · Daily log</h1>
  <div class="sub">${f === t ? fmtDay(f) : `${fmtDay(f)} – ${fmtDay(t)}`} · printed ${fmtDMYTime(new Date())}</div>${sums}
  <table><thead><tr>${HEAD.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>
  ${rows.map(r => `<tr>${r.map((c, i) => `<td class="${i === 6 ? esc(c.split(' ')[0]) : ''}">${esc(c)}</td>`).join('')}</tr>`).join('')}
  </tbody></table><script>window.onload = () => window.print();</script></body></html>`);
  w.document.close();
}

function saveCsv(rows: string[][], name: string) {
  const csv = '﻿' + rows.map(r => r.map(csvEscape).join(',')).join('\r\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}
function printTable(title: string, head: string[], rows: string[][]) {
  const esc = (s: string) => s.replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c]!);
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>
    @page { size: A4; margin: 12mm; } body { font-family: Segoe UI, Arial, sans-serif; font-size: 11px; color: #222; }
    h1 { font-size: 16px; margin: 0 0 6px; } table { width: 100%; border-collapse: collapse; } th, td { border-bottom: 1px solid #ddd; padding: 3px 4px; text-align: left; }
    th { background: #f0f3f8; }
  </style></head><body><h1>Bhorosha · ${esc(title)}</h1><div style="color:#666;margin-bottom:6px">Printed ${fmtDMYTime(new Date())}</div>
  <table><thead><tr>${head.map(h => `<th>${esc(h)}</th>`).join('')}</tr></thead><tbody>
  ${rows.map(r => `<tr>${r.map(c => `<td>${esc(c)}</td>`).join('')}</tr>`).join('')}
  </tbody></table><script>window.onload = () => window.print();</script></body></html>`);
  w.document.close();
}
